import { sourceFormConfig } from "../../../../app/components/appwizard/component-wizards/wizards/source/database-reader/configs/source-form-info.js";
import api from "../../../../core/api/api";
import TARGET_FORM_INFO from "../target/target-form-info";
import { validationUtils } from "../utils";
import { v4 } from "uuid";
import { targetInfoType } from "../target/Types/index.js";
import { DB_PROVIDERS } from "../../../../app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers.js";
import { isAutomatedWizard } from "../../../../app/components/appwizard/component-wizards/wizards/source/database-reader/app-services";

/**
 * @param obj
 * Checks whether the given arg is a backbone model
 * if not assign the get method to it
 */
const addMethods = (obj: any) => {
    if (!obj) return;
    if (!obj.cid) {
        obj.get = function(propertyName: string): void {
            return obj?.[propertyName];
        };
        obj.set = function(propertyName: string, data: any): void {
            obj[propertyName] = data;
        };
        obj.cid = v4();
    }
};

/**
 * Checks whether the connection URL needs to be fetched based on adapter and useConnectionProfile.
 * For OLTP adapters using a connection profile, fetching the connection URL is skipped since the URL is already available in CP.
 * @param useConnectionProfile
 * @param adapter
 * @returns boolean
 */
export function shouldFetchConnectionURL(useConnectionProfile: boolean, adapter: string) {
    const isOLTPEndpoint = validationUtils.isCPAvailableForOLTP(adapter);
    return !(useConnectionProfile && isOLTPEndpoint);
}

function computeSSLConfig(adapterName: string, connectionParams, isTarget: boolean): string {
    addMethods(connectionParams);
    if (!connectionParams.get("useSSL")) return "";
    const hasValue = (v: unknown) => v != null && v !== "";
    let fieldsToAdd;
    if (isTarget) {
        fieldsToAdd = TARGET_FORM_INFO[adapterName].form_fields.filter(
            field => field.addToTQL && hasValue(connectionParams.get(field.name))
        );
    } else {
        fieldsToAdd =
            sourceFormConfig[adapterName]?.ilcdc_controls?.form_fields.filter(
                field => field.addToTQL && hasValue(connectionParams.get(field.name))
            ) ?? [];
    }
    return fieldsToAdd.map(field => `javax.net.ssl.${field.name}=${connectionParams.get(field.name)}`).join(";");
}

function getSSlParams(adapter: string, connectionParams, commonParams) {
    const useSSL = connectionParams.get("useSSL");
    adapter = adapter.toUpperCase();
    switch (adapter) {
        case "ORACLE":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        trustStore: connectionParams.get("trustStore"),
                        trustStoreType: connectionParams.get("trustStoreType"),
                        trustStorePassword: connectionParams.get("trustStorePassword"),
                        keyStore: connectionParams.get("keyStore") || undefined,
                        keyStoreType: connectionParams.get("keyStoreType") || undefined,
                        keyStorePassword: connectionParams.get("keyStorePassword") || undefined
                    }
                })
            };
        case "OJET":
        case "OJETCDC":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        serverCertificateDomain: connectionParams.get("serverCertificateDomain"),
                        walletLocation: connectionParams.get("walletLocation")
                    }
                })
            };
        case "POSTGRES":
        case "YUGABYTE":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        useSSL: connectionParams.get("useSSL"),
                        sslmode: connectionParams.get("sslmode"),
                        sslcert: connectionParams.get("sslcert"),
                        sslkeypk: connectionParams.get("sslkeypk"),
                        sslrootcert: connectionParams.get("sslrootcert")
                    }
                })
            };
        case "MSSQL":
        case "MSJET":
        case "SQLSERVER":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        encrypt: true,
                        trustServerCertificate: connectionParams.get("trustServerCertificate") || false,
                        trustStore: connectionParams.get("trustStore"),
                        trustStorePassword: connectionParams.get("trustStorePassword"),
                        hostNameInCertificate: connectionParams.get("hostNameInCertificate")
                    }
                })
            };
        case "MYSQL":
        case "MARIADB":
        case "MARIADBXPAND":
            return {
                ...commonParams,
                ...(useSSL && {
                    sslConfigMap: {
                        useSSL: connectionParams.get("useSSL"),
                        requireSSL: connectionParams.get("requireSSL") || false,
                        verifyServerCertificate: connectionParams.get("verifyServerCertificate") || false,
                        trustCertificateKeyStoreUrl: connectionParams.get("trustCertificateKeyStoreUrl"),
                        trustCertificateKeyStoreType: connectionParams.get("trustCertificateKeyStoreType"),
                        trustCertificateKeyStorePassword: connectionParams.get("trustCertificateKeyStorePassword"),
                        clientCertificateKeyStoreUrl: connectionParams.get("clientCertificateKeyStoreUrl") || undefined,
                        clientCertificateKeyStoreType:
                            connectionParams.get("clientCertificateKeyStoreType") || undefined,
                        clientCertificateKeyStorePassword:
                            connectionParams.get("clientCertificateKeyStorePassword") || undefined
                    }
                })
            };
        default:
            return null;
    }
}
async function getParams(
    adapter: string,
    connectionParams: any,
    getPrimaryParams: boolean = false,
    isTarget: boolean = false
): Promise<Object> {
    addMethods(connectionParams);
    const params = await buildParams(adapter, connectionParams, getPrimaryParams, isTarget);
    const sslConfigString = connectionParams.get("SSLConfig");
    if (sslConfigString && params && typeof params === "object" && !("SSLConfig" in params)) {
        (params as Record<string, unknown>).SSLConfig = sslConfigString;
    }
    return params;
}

async function buildParams(
    adapter: string,
    connectionParams: any,
    getPrimaryParams: boolean,
    isTarget: boolean
): Promise<Object> {
    // For Non ILCDC Source types
    if (!validationUtils.isAutomatedILCDCSource(adapter.toUpperCase()) && !isTarget) {
        return {
            UserName: connectionParams.get("username"),
            Password: connectionParams.get("password"),
            ConnectionURL: connectionParams.getJDBCURL()
        };
    }
    if (adapter === DB_PROVIDERS.SALESFORCE.id) {
        return {
            authMethod: connectionParams.get("authMethod"),
            autoAuthTokenRenewal: connectionParams.get("authMethod") !== "authToken",
            OAuthAuthorizationFlows: connectionParams.get("authMethod"),
            username: connectionParams.get("username"),
            password: connectionParams.get("password"),
            securityToken: connectionParams.get("securityToken"),
            consumerKey: connectionParams.get("consumerKey"),
            consumerSecret: connectionParams.get("consumerSecret"),
            accesstoken: connectionParams.get("authToken"),
            apiEndPoint: connectionParams.get("host"),
            JWTKeystorePath: connectionParams.get("JWTKeystorePath"),
            JWTKeystorePassword: connectionParams.get("JWTKeystorePassword"),
            JWTCertificateName: connectionParams.get("JWTCertificateName")
        };
    } else if (adapter === DB_PROVIDERS.OJET.id) {
        let connURL;
        if (connectionParams.get("supportDownStreamCapture") && getPrimaryParams) {
            const useConnectionProfile = connectionParams.get("useConnectionProfile");
            const connectionProfileName = connectionParams.get("connectionProfileName");
            const supportDownStreamCapture = connectionParams.get("supportDownStreamCapture");
            const primaryConnectionProfileName = connectionParams.get("primaryConnectionProfileName");

            connURL =
                connectionParams.get("downstreamConnectionURL") ?? (await getDownStreamConnectionURL(connectionParams));
            connectionParams.set("downstreamConnectionURL", connURL);
            let password = connectionParams.get("primaryDatabasePassword");
            if (connectionParams.get("encrypted") && typeof password !== "object") {
                password = { encrypted: password };
            }
            const commonParams = {
                UserName: connectionParams.get("primaryDatabaseUsername"),
                Password: password,
                ConnectionURL: connURL,
                encrypted: connectionParams.get("encrypted") || false,
                containerName: connectionParams.get("containerName") || undefined,
                sid: connectionParams.get("primaryDatabaseServiceName"),
                service: connectionParams.get("service"),
                useConnectionProfile,
                connectionProfileName: supportDownStreamCapture ? primaryConnectionProfileName : connectionProfileName,
                DownstreamCapture: supportDownStreamCapture
            };
            return getSSlParams(adapter, connectionParams, commonParams);
        } else {
            const useConnectionProfile = connectionParams.get("useConnectionProfile");
            const connectionProfileName = connectionParams.get("connectionProfileName");

            const connURL =
                connectionParams.get("connectionUrl") ?? (await getConnectionUrl(adapter, connectionParams));
            connectionParams.set("connectionUrl", connURL);
            let password = connectionParams.get("password");
            if (connectionParams.get("encrypted") && typeof password !== "object") {
                password = { encrypted: password };
            }
            const commonParams = {
                UserName: connectionParams.get("username"),
                Password: password,
                ConnectionURL: connURL,
                encrypted: connectionParams.get("encrypted") || false,
                containerName: connectionParams.get("containerName") || undefined,
                service: connectionParams.get("service"),
                sid: connectionParams.get("sid"),
                useConnectionProfile,
                connectionProfileName
            };

            return getSSlParams(adapter, connectionParams, commonParams);
        }
    } else {
        const useConnectionProfile = connectionParams.get("useConnectionProfile");
        const connectionProfileName = connectionParams.get("connectionProfileName");
        let connURL;
        if (adapter.toUpperCase() === DB_PROVIDERS.POSTGRES.id) {
            connURL = connectionParams.get("connectionUrl");
            if (connURL && !connURL.toLowerCase().startsWith("jdbc:")) {
                // Parse the raw connection URL to extract host, port, and dbName if not already present
                // Format: "host:port/database" or "host:port"
                if (!connectionParams.get("host") || !connectionParams.get("port")) {
                    const match = connURL.match(/^([^:]+):(\d+)(?:\/(.+))?$/);
                    if (match) {
                        if (!connectionParams.get("host")) {
                            connectionParams.set("host", match[1]);
                        }
                        if (!connectionParams.get("port")) {
                            connectionParams.set("port", parseInt(match[2]));
                        }
                        if (match[3] && !connectionParams.get("dbName")) {
                            connectionParams.set("dbName", match[3]);
                        }
                    }
                }
                connURL = await getConnectionUrl(adapter, connectionParams);
            } else if (!connURL) {
                // If no connectionUrl exists, generate it
                connURL = await getConnectionUrl(adapter, connectionParams);
            }
        } else {
            connURL = connectionParams.get("connectionUrl") ?? (await getConnectionUrl(adapter, connectionParams));
        }
        connectionParams.set("connectionUrl", connURL);

        const commonParams = {
            UserName: connectionParams.get("username"),
            Password: connectionParams.get("encrypted")
                ? { encrypted: connectionParams.get("password") }
                : connectionParams.get("password"),
            ConnectionURL: connURL,
            encrypted: connectionParams.get("encrypted") || false,
            containerName: connectionParams.get("containerName") || undefined,
            service: connectionParams.get("service"),
            useConnectionProfile,
            connectionProfileName
        };

        const integratedSecurity = connectionParams.get("integratedSecurity");
        integratedSecurity && (commonParams["integratedSecurity"] = integratedSecurity);

        return getSSlParams(adapter, connectionParams, commonParams);
    }
}

function normalizeWizardType(adapter: string): string {
    const type = adapter.toUpperCase();
    const isILCDC = isAutomatedWizard();
    if (type === "SQLSERVER" || type === "MSJET") return "MSSQL";
    if (type === "OJET") return isILCDC ? "ORACLE" : "OJET";
    if (type === "OJETCDC") return "OJET";
    if (type === "YUGABYTEDB") return "YUGABYTE";
    return type;
}

async function getDownStreamConnectionURL(connectionParams) {
    addMethods(connectionParams);
    let commonParams;

    const canFetchConnectionURL = shouldFetchConnectionURL(
        connectionParams.get("useConnectionProfile"),
        DB_PROVIDERS.OJET.id
    );
    if (!canFetchConnectionURL) return null;

    if (connectionParams.get("supportDownStreamCapture")) {
        commonParams = {
            hostName: connectionParams.get("primaryDatabaseHostname"),
            port: connectionParams.get("primaryDatabasePort"),
            sid: connectionParams.get("primaryDatabaseServiceName"),
            containerName: connectionParams.get("containerName")
        };
    } else {
        commonParams = {
            service: connectionParams.get("service"),
            containerName: connectionParams.get("containerName"),
            hostName: connectionParams.get("host"),
            port: connectionParams.get("port"),
            sid: connectionParams.get("sid")
        };
    }
    const APIParams = getSSlParams("OJET", connectionParams, commonParams);
    return await api.WizardHelper_GetConnectionURL("ORACLE", APIParams);
}
async function getConnectionUrl(adapter: string, connectionParams: any) {
    if (!connectionParams) return null;

    addMethods(connectionParams);

    const canFetchConnectionURL = shouldFetchConnectionURL(connectionParams.get("useConnectionProfile"), adapter);
    if (!canFetchConnectionURL) {
        return null;
    }

    const commonParams = {
        streamingUpload: null,
        catalog: null,
        service: connectionParams.get("service"),
        containerName: connectionParams.get("containerName"),
        hostName: connectionParams.get("host"),
        port: connectionParams.get("port"),
        dbName: connectionParams.get("dbName"),
        sid: connectionParams.get("sid"),
        integratedSecurity: connectionParams.get("integratedSecurity") || false
    };
    adapter = adapter.toUpperCase();
    const APIParams = getSSlParams(adapter, connectionParams, commonParams);
    return await api.WizardHelper_GetConnectionURL(normalizeWizardType(adapter), APIParams);
}

async function getEncryptedUrl(
    adapter: string,
    connectionParams: any,
    appName: string,
    agentName: string | undefined = undefined
) {
    if (!connectionParams) return null;

    addMethods(connectionParams);

    const canFetchEncryptedURL = shouldFetchConnectionURL(connectionParams.get("useConnectionProfile"), adapter);
    if (!canFetchEncryptedURL) {
        return null;
    }

    const commonParams = {
        streamingUpload: null,
        catalog: null,
        service: connectionParams.get("service"),
        containerName: connectionParams.get("containerName"),
        hostName: connectionParams.get("host"),
        port: connectionParams.get("port"),
        dbName: connectionParams.get("dbName"),
        sid: connectionParams.get("sid"),
        integratedSecurity: connectionParams.get("integratedSecurity") || false
    };
    const APIParams = getSSlParams(adapter, connectionParams, commonParams);
    return await api.WizardHelper_GetEncryptedConnectionURL(
        normalizeWizardType(adapter),
        APIParams,
        appName,
        agentName
    );
}

function setParalleThreadsForSource(sourceModel, targetInfo: targetInfoType): void {
    try {
        const parallelThreads = targetInfo.targetFormValues.ParallelThreads;
        if (parallelThreads) {
            sourceModel.adapter.properties.ParallelThreads = parallelThreads;
        }
    } catch (error) {
        console.error("Coudn't set parallel threads property for source model", error);
    }
}

function getNameAndSchema(table: string, type: string): [string, string] {
    let schema, name;
    let chunks = table.split(".");
    if (![DB_PROVIDERS.MONGODB.id, DB_PROVIDERS.MARIADBXPAND.id].includes(type) && chunks.length === 3) {
        chunks.shift();
        schema = chunks.shift();
    } else {
        schema = chunks.shift();
    }
    name = chunks.join(".");
    return [name, schema];
}

export {
    computeSSLConfig,
    getParams,
    getConnectionUrl,
    getEncryptedUrl,
    getDownStreamConnectionURL,
    setParalleThreadsForSource,
    addMethods,
    getNameAndSchema,
    normalizeWizardType
};
